import { Component, ElementRef, HostListener, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DocumentNodeType, DocumentPermission, DocumentStatus, DocumentViewLayout, FileTypeCategory, SelectionPrimaryKeys } from '../../../../core/enums/data.enums';
import { DocumentsPermission } from '../../../../core/enums/permissions.enums';
import { DocumentListSortOptions } from '../../../../core/enums/sort-options.enums';
import { DocumentNodeModel } from '../../../../core/models/data/document.model';
import { DocumentUtilities } from '../../../../core/utilities/document.utilities';
import { OrgDatePipe } from '../../../../core/pipes/org-date/org-date.pipe';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { DocumentService } from '../../../../core/services/document.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { DropdownActions } from '../../../../core/enums/base.enums';
import { DocumentsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { PaginationConstants } from '../../../../core/constants/constants';
import { MobileCardCheckbox } from '../../../../parts/mobile-card-checkbox/mobile-card-checkbox';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';

// Row-level actions emitted from the per-item action menu.
export enum DocumentRowAction {
    Download = 'download',
    Rename = 'rename',
    Move = 'move',
    Permissions = 'permissions',
    Delete = 'delete'
}

interface DocumentListItem {
    node: DocumentNodeModel;
}

@Component({
    selector: 'ms-document-list',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule, OrgDatePipe, NoValuePipe, MobileCardCheckbox, InViewPortDirective, IfHasPermissionDirective, TooltipDirective, ResponsiveTableDirective, ResponsiveTableColumnDirective],
    templateUrl: './document-list.component.html',
    styleUrl: './document-list.component.scss'
})
export class DocumentListComponent {

    public items = input<DocumentListItem[]>([]);
    // Folders and files are separate backend entities with independent id sequences (FolderModel.folderId
    // vs DocumentModel.documentId) — a folder and a file can legitimately share the same documentNodeId.
    // Kept as two separate sets (rather than one shared Set<number>) so selecting one never bleeds into
    // the other just because their ids happen to collide (DEV-2572).
    public selectedFolderIds = input<Set<number>>(new Set<number>());
    public selectedFileIds = input<Set<number>>(new Set<number>());
    public isLoading = input<boolean>(false);
    // Desktop/tablet row/column layout for the listing below (DEV-2559). Mobile ignores this entirely.
    public viewLayout = input<DocumentViewLayout>(DocumentViewLayout.List);
    public showBreadcrumbColumn = input<boolean>(false);
    // Checked state for the "select all folders" / "select all files" header controls (DEV-2481).
    public allFoldersSelected = input<boolean>(false);
    public allFilesSelected = input<boolean>(false);
    // True at the document-center root (no current folder), where files can't be uploaded directly —
    // drives the root-specific empty-state copy.
    public isRoot = input<boolean>(false);
    // File pagination is page-driven: the page fetches file pages from the API and tells the list
    // whether more remain / a page is loading. The sentinel below the file grid requests the next.
    public hasMoreFiles = input<boolean>(false);
    public isLoadingMoreFiles = input<boolean>(false);
    // documentNodeId of the file currently downloading — drives its spinner + disabled state.
    public downloadingId = input<number | undefined>(undefined);
    // Independent per-table sort for List view's own column headers (DEV-2572). Grid/Mobile don't read
    // these at all — they have no per-column headers, so they stay on whatever the toolbar last set.
    public folderSort = input<DocumentListSortOptions>(DocumentListSortOptions.NameAsc);
    public fileSort = input<DocumentListSortOptions>(DocumentListSortOptions.NameAsc);
    // List view's file rows — may be the page's diverged (independently re-fetched) array once the
    // file table's own header has been used, or just mirror `items()`'s files otherwise. Grid/Mobile's
    // file tiles keep reading `items()` via `fileItems()` below, never this.
    public listFileItems = input<DocumentListItem[]>([]);
    // Drives an in-place dim+spinner on the file table while a diverged re-sort is in flight, instead
    // of the full-page isLoading() skeleton swap Grid/Mobile still use.
    public isFilesRefreshing = input<boolean>(false);
    public listHasMoreFiles = input<boolean>(false);
    public listIsLoadingMoreFiles = input<boolean>(false);

    public folderOpened = output<DocumentNodeModel>();
    public fileOpened = output<DocumentNodeModel>();
    public selectionToggled = output<{ documentNodeId: number, type: DocumentNodeType, checked: boolean }>();
    public selectAllFoldersToggled = output<boolean>();
    public selectAllFilesToggled = output<boolean>();
    public downloadRequested = output<DocumentNodeModel>();
    public renameRequested = output<DocumentNodeModel>();
    public moveRequested = output<DocumentNodeModel>();
    public permissionsRequested = output<DocumentNodeModel>();
    public deleteRequested = output<DocumentNodeModel>();
    public loadMoreFiles = output<void>();
    // Separate from loadMoreFiles — List view's file sentinel may be paging through the page's diverged
    // fetch instead of the shared one Grid/Mobile use, and the two must never be conflated (DEV-2572).
    public loadMoreListFiles = output<void>();
    public folderSortChanged = output<DocumentListSortOptions>();
    public fileSortChanged = output<DocumentListSortOptions>();

    public DocumentNodeType = DocumentNodeType;
    public DocumentViewLayout = DocumentViewLayout;
    public DocumentPermission = DocumentPermission;
    public DocumentStatus = DocumentStatus;
    public DocumentsPermission = DocumentsPermission;
    public DocumentRowAction = DocumentRowAction;
    public DocumentsMessagingConstants = DocumentsMessagingConstants;
    public SelectionPrimaryKeys = SelectionPrimaryKeys;
    public DocumentUtilities = DocumentUtilities;
    public DocumentListSortOptions = DocumentListSortOptions;

    public skeletonTiles = Array.from({ length: 8 }, (_, i) => i);
    // A few placeholder file cards shown appended to the grid while the next file page loads.
    public loadMoreSkeletons = Array.from({ length: 4 }, (_, i) => i);

    public folderItems = computed<DocumentListItem[]>(() =>
        this.items().filter(i => i.node.type === DocumentNodeType.Folder)
    );

    public fileItems = computed<DocumentListItem[]>(() =>
        this.items().filter(i => i.node.type === DocumentNodeType.File)
    );

    // List view's folder table only — a pure client-side re-order of the already-loaded folders
    // (never a fetch: folders are always eager-loaded in full). Grid view's tiles keep using the
    // unsorted folderItems() above (DEV-2572). folderSort() is always clamped to Name/Modified by the
    // page's clampSortToFolderContext, so those are the only cases that matter here.
    public listFolderItems = computed<DocumentListItem[]>(() => {
        const sort = this.folderSort();
        const items = [...this.folderItems()];
        switch (sort) {
            case DocumentListSortOptions.NameDesc:
                return items.sort((a, b) => b.node.name.localeCompare(a.node.name));
            case DocumentListSortOptions.LastModifiedAsc:
                return items.sort((a, b) => new Date(a.node.modifiedDate).getTime() - new Date(b.node.modifiedDate).getTime());
            case DocumentListSortOptions.LastModifiedDesc:
                return items.sort((a, b) => new Date(b.node.modifiedDate).getTime() - new Date(a.node.modifiedDate).getTime());
            case DocumentListSortOptions.NameAsc:
            default:
                return items.sort((a, b) => a.node.name.localeCompare(b.node.name));
        }
    });

    private elementRef = inject(ElementRef<HTMLElement>);
    private bootstrapService = inject(BootstrapService);
    private authorizationService = inject(AuthorizationService);
    private authorizationStateService = inject(AuthorizationStateService);
    private userSessionService = inject(UserSessionService);

    // Selection (folder checkboxes) feeds the bulk "Change permissions" action, so it's shown to
    // users who can manage permissions; everyone else sees the plain folder icon.
    public canManagePermissions = computed<boolean>(() => {
        const state = this.authorizationStateService.authorizationState();
        if (!state) return false;
        const organizationId = this.userSessionService.activeOrganization()?.organizationId;
        return this.authorizationService.hasPermission(state, DocumentsPermission.ManagePermissions, organizationId);
    });

    // File checkboxes enable the bulk "Move to…" and bulk delete actions, so they're shown only to
    // users who can manage documents; everyone else sees the plain file tile.
    public canManageDocuments = computed<boolean>(() => {
        const state = this.authorizationStateService.authorizationState();
        if (!state) return false;
        const organizationId = this.userSessionService.activeOrganization()?.organizationId;
        return this.authorizationService.hasPermission(state, DocumentsPermission.Manage, organizationId);
    });

    // Folder checkboxes now feed bulk delete (documents.manage) as well as bulk "Change permissions"
    // (documents.manage_permissions) — a user with either permission needs to see them (DEV-2481).
    public canSelectFolders = computed<boolean>(() => this.canManagePermissions() || this.canManageDocuments());

    // Latch so the file sentinel loads one page at a time (re-armed after a debounce) instead of
    // cascading through every page while it sits in the viewport.
    private canLoadMoreFiles = true;
    // Separate latch for List view's own sentinel — it may be paging through a different (diverged)
    // fetch than Grid/Mobile's, so the two debounces must never share state (DEV-2572).
    private canLoadMoreListFiles = true;

    constructor(public documentService: DocumentService) {}

    public onFilesSentinelInView(): void {
        if (!this.canLoadMoreFiles || !this.hasMoreFiles() || this.isLoadingMoreFiles()) return;
        this.canLoadMoreFiles = false;
        this.loadMoreFiles.emit();
        setTimeout(() => { this.canLoadMoreFiles = true; }, PaginationConstants.LOAD_MORE_DEBOUNCE_TIME_MILLISECONDS());
    }

    public onListFilesSentinelInView(): void {
        if (!this.canLoadMoreListFiles || !this.listHasMoreFiles() || this.listIsLoadingMoreFiles()) return;
        this.canLoadMoreListFiles = false;
        this.loadMoreListFiles.emit();
        setTimeout(() => { this.canLoadMoreListFiles = true; }, PaginationConstants.LOAD_MORE_DEBOUNCE_TIME_MILLISECONDS());
    }

    // Close any open tile dropdown on window scroll so it doesn't drift along with the tile
    // as the user scrolls the page. Matches the responsive-table directive's
    // close-on-container-scroll behavior.
    @HostListener('window:scroll')
    public onWindowScroll(): void {
        const host = this.elementRef.nativeElement as HTMLElement;
        const openToggles = host.querySelectorAll('[data-bs-toggle="dropdown"][aria-expanded="true"]') as NodeListOf<HTMLElement>;
        openToggles.forEach(toggle => this.bootstrapService.manageDropdown(toggle, DropdownActions.Hide));
    }

    public isSelected(node: DocumentNodeModel): boolean {
        return node.type === DocumentNodeType.Folder
            ? this.selectedFolderIds().has(node.documentNodeId)
            : this.selectedFileIds().has(node.documentNodeId);
    }

    public onRowClicked(node: DocumentNodeModel, event?: Event): void {
        // Skip when the click came from an interactive sub-region (checkbox, kebab, dropdown
        // menu item). We deliberately don't stopPropagation on those wrappers so the click can
        // bubble to the document — Bootstrap relies on that to close other open dropdowns.
        if (event) {
            const target = event.target as HTMLElement | null;
            if (target?.closest('.folder-tile-actions, .folder-tile-checkbox, .tile-actions, .tile-checkbox, .dropdown-menu')) {
                return;
            }
        }
        if (node.type === DocumentNodeType.Folder) {
            this.folderOpened.emit(node);
        } else if (this.downloadingId() == null && DocumentUtilities.isFileAvailable(node)) {
            // One download at a time, and only once the scan clears: ignore clicks on files that are
            // still downloading, Pending, or Quarantined (the status badge's tooltip explains why).
            this.fileOpened.emit(node);
        }
    }

    // Tooltip for a non-available file's status badge — the user-facing reason it can't be downloaded.
    public statusTooltip(node: DocumentNodeModel): string {
        switch (node.status) {
            case DocumentStatus.Pending:
                return DocumentsMessagingConstants.DOCUMENTS_STATUS_PENDING_TOOLTIP();
            case DocumentStatus.Quarantined:
                return DocumentsMessagingConstants.DOCUMENTS_STATUS_QUARANTINED_TOOLTIP();
            default:
                return '';
        }
    }

    public onCheckboxClicked(event: Event, documentNodeId: number, type: DocumentNodeType): void {
        event.stopPropagation();
        const checkbox = event.target as HTMLInputElement;
        this.selectionToggled.emit({ documentNodeId, type, checked: checkbox.checked });
    }

    public onMobileCheckboxClicked(event: { id: number | undefined, checked: boolean }, type: DocumentNodeType): void {
        if (event.id == null) return;
        this.selectionToggled.emit({ documentNodeId: event.id, type, checked: event.checked });
    }

    public onSelectAllFoldersClicked(event: Event): void {
        const checkbox = event.target as HTMLInputElement;
        this.selectAllFoldersToggled.emit(checkbox.checked);
    }

    public onSelectAllFilesClicked(event: Event): void {
        const checkbox = event.target as HTMLInputElement;
        this.selectAllFilesToggled.emit(checkbox.checked);
    }

    // Sortable column headers (list view) — folders and files toggle independently (DEV-2572).
    private static readonly NAME_MODIFIED_SORT_MAP: Record<string, [DocumentListSortOptions, DocumentListSortOptions]> = {
        'Name': [DocumentListSortOptions.NameAsc, DocumentListSortOptions.NameDesc],
        'Modified': [DocumentListSortOptions.LastModifiedAsc, DocumentListSortOptions.LastModifiedDesc]
    };

    // Folders have no API-backed Size/Type sort (see DocumentService.mapToFolderSort), so their header
    // click map only ever offers Name/Modified.
    public toggleFolderSortDirection(columnName: 'Name' | 'Modified'): void {
        this.folderSortChanged.emit(this.nextSortDirection(this.folderSort(), DocumentListComponent.NAME_MODIFIED_SORT_MAP[columnName]));
    }

    public toggleFileSortDirection(columnName: 'Name' | 'Modified' | 'Type' | 'Size'): void {
        const columnSortMap: Record<string, [DocumentListSortOptions, DocumentListSortOptions]> = {
            ...DocumentListComponent.NAME_MODIFIED_SORT_MAP,
            'Size': [DocumentListSortOptions.SizeAsc, DocumentListSortOptions.SizeDesc],
            'Type': [DocumentListSortOptions.TypeAsc, DocumentListSortOptions.TypeDesc]
        };
        this.fileSortChanged.emit(this.nextSortDirection(this.fileSort(), columnSortMap[columnName]));
    }

    private nextSortDirection(current: DocumentListSortOptions, [ascOption, descOption]: [DocumentListSortOptions, DocumentListSortOptions]): DocumentListSortOptions {
        if (current === descOption) return ascOption;
        if (current === ascOption) return descOption;
        return ascOption;
    }

    // The click is deliberately left to bubble: onRowClicked already skips anything from inside a
    // .dropdown-menu, and Bootstrap needs it to reach the document to close the menu (DEV-2806).
    public onActionClicked(action: DocumentRowAction, node: DocumentNodeModel): void {
        switch (action) {
            case DocumentRowAction.Download:
                this.downloadRequested.emit(node);
                break;
            case DocumentRowAction.Rename:
                this.renameRequested.emit(node);
                break;
            case DocumentRowAction.Move:
                this.moveRequested.emit(node);
                break;
            case DocumentRowAction.Permissions:
                this.permissionsRequested.emit(node);
                break;
            case DocumentRowAction.Delete:
                this.deleteRequested.emit(node);
                break;
        }
    }

    // Trust the API's isEmpty flag when present, otherwise derive it from the counts so the
    // empty/contents glyph stays correct even if the backend omits the flag.
    public isFolderEmpty(node: DocumentNodeModel): boolean {
        if (typeof node.isEmpty === 'boolean') return node.isEmpty;
        return (node.childFolderCount ?? 0) === 0 && (node.childFileCount ?? 0) === 0;
    }

    public getNodeIcon(node: DocumentNodeModel): string {
        // A locked, system-managed folder (e.g. Email Attachments) gets its own glyph — a gear reads
        // as "system-managed" without borrowing the plain padlock already used for AdminOnly
        // permission elsewhere on the tile (DEV-2572). `folder-lock` isn't in this kit's icon set.
        if (node.type === DocumentNodeType.Folder && node.isLocked) return 'folder-gear';
        // Empty folders get the open folder glyph (reads as "nothing inside"); folders with
        // contents get the closed folder glyph. (In this FA kit `folder` and `folder-blank`
        // share identical path data, so `folder-open` is the distinct silhouette.)
        if (node.type === DocumentNodeType.Folder) return this.isFolderEmpty(node) ? 'folder-open' : 'folder';
        switch (node.fileTypeCategory) {
            case FileTypeCategory.Pdf: return 'file-pdf';
            case FileTypeCategory.Image: return 'file-image';
            case FileTypeCategory.Document: return 'file-word';
            case FileTypeCategory.Spreadsheet: return 'file-spreadsheet';
            case FileTypeCategory.Video: return 'file-video';
            case FileTypeCategory.Archive: return 'file-zipper';
            default: return 'file';
        }
    }

    // File-type glyphs read better lighter; the empty-folder (open) glyph is a touch lighter than
    // a filled folder. folder-gear reads better inverted from the usual duotone balance — a bolder
    // secondary layer with the primary layer dimmed below it — or the cog gets lost against the
    // folder outline; see getNodeIconPrimaryOpacity below, which must be bound alongside this.
    public getNodeIconSecondaryOpacity(node: DocumentNodeModel): string {
        if (node.type === DocumentNodeType.File) return '0.4';
        if (node.isLocked) return '1';
        return this.isFolderEmpty(node) ? '0.5' : '0.9';
    }

    // Only folder-gear (locked folders) needs this dimmed — every other glyph keeps the default
    // full-opacity primary layer. Pushed well below the secondary layer's opacity so the cog reads
    // clearly against the folder outline instead of blending into it.
    public getNodeIconPrimaryOpacity(node: DocumentNodeModel): string {
        return node.type === DocumentNodeType.Folder && node.isLocked ? '0.15' : '1';
    }

    public getNodeIconClass(node: DocumentNodeModel): string {
        if (node.type === DocumentNodeType.Folder) return 'node-icon-folder';
        switch (node.fileTypeCategory) {
            case FileTypeCategory.Pdf: return 'node-icon-pdf';
            case FileTypeCategory.Image: return 'node-icon-image';
            case FileTypeCategory.Document: return 'node-icon-doc';
            case FileTypeCategory.Spreadsheet: return 'node-icon-spreadsheet';
            default: return 'node-icon-other';
        }
    }

    public formatBytes(bytes: number | undefined): string {
        return this.documentService.formatBytes(bytes);
    }
}
